import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { EmptyState } from '../../components/ui/empty-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const AdminQuotes = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [notes, setNotes] = useState({});
  const [rejectDialog, setRejectDialog] = useState({ open: false, quote: null });
  const [processing, setProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const endpoint = filterStatus === 'pending' ? '/admin/quotes/pending' : '/admin/quotes/all';
        const response = await api.get(endpoint);
        setQuotes(response.data);
      } catch (error) {
        toast.error('Offertes laden mislukt');
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, [api, filterStatus]);

  const filteredQuotes = quotes.filter(quote => {
    const searchLower = searchQuery.toLowerCase();
    return (
      quote.action_title?.toLowerCase().includes(searchLower) ||
      quote.partner_name?.toLowerCase().includes(searchLower) ||
      quote.client_name?.toLowerCase().includes(searchLower)
    );
  });

  const handleReview = async (quoteId, status) => {
    setProcessing(true);
    try {
      await api.patch(`/admin/quotes/${quoteId}/review`, {
        status,
        admin_notes: notes[quoteId] || null
      });
      setQuotes(quotes.filter(q => q.id !== quoteId));
      toast.success(status === 'approved' ? 'Offerte goedgekeurd' : 'Offerte afgewezen');
      setRejectDialog({ open: false, quote: null });
    } catch (error) {
      toast.error('Actie mislukt');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClick = (quote) => {
    setRejectDialog({ open: true, quote });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Onbekend';
    try {
      return new Date(dateString).toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Onbekend';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-black">
            Offertes
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            Offertes die buiten de standaard prijslijst vallen vereisen handmatige goedkeuring.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
            <Input
              type="text"
              placeholder="Zoek op titel, partner of klant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-neutral-200"
              data-testid="quotes-search-input"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[180px] border-neutral-200">
              <Filter className="h-4 w-4 mr-2 text-neutral-400" strokeWidth={1.5} />
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">In afwachting</SelectItem>
              <SelectItem value="all">Alle offertes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Quotes List */}
        {filteredQuotes.length > 0 ? (
          <div className="space-y-6">
            {filteredQuotes.map((quote) => (
              <Card 
                key={quote.id} 
                className="border-neutral-200 border-l-4 border-l-orange-400 shadow-[0_2px_4px_rgba(0,0,0,0.04)]"
                data-testid={`quote-card-${quote.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Quote Info */}
                    <div className="flex-1 space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                          <AlertCircle className="h-6 w-6 text-orange-600" strokeWidth={1.5} />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-black">{quote.action_title}</h3>
                          <p className="text-sm text-neutral-600 mt-1">
                            <span className="font-medium">{quote.partner_name}</span> → {quote.client_name}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-neutral-50 rounded-lg">
                        <div>
                          <p className="text-xs text-neutral-500">Bedrag</p>
                          <p className="text-lg font-semibold text-black">{formatCurrency(quote.amount)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Doorlooptijd</p>
                          <p className="text-lg font-semibold text-black">{quote.estimated_days} dagen</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Status</p>
                          <Badge className="border-transparent bg-orange-50 text-orange-700">
                            <Clock className="h-3 w-3 mr-1" strokeWidth={1.5} />
                            In review
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500">Ingediend</p>
                          <p className="text-sm text-neutral-600">{formatDate(quote.created_at)}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-neutral-500 mb-1">Beschrijving</p>
                        <p className="text-sm text-neutral-700">{quote.description}</p>
                      </div>

                      {/* Admin Notes */}
                      <div>
                        <p className="text-xs text-neutral-500 mb-1">Admin notities (optioneel)</p>
                        <Textarea
                          placeholder="Notities voor partner (bijv. reden afwijzing)..."
                          value={notes[quote.id] || ''}
                          onChange={(e) => setNotes({ ...notes, [quote.id]: e.target.value })}
                          className="border-neutral-200 focus:border-black focus:ring-black"
                          rows={2}
                          data-testid={`notes-textarea-${quote.id}`}
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-row lg:flex-col gap-2 lg:w-32">
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleReview(quote.id, 'approved')}
                        data-testid={`approve-button-${quote.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />
                        Goedkeuren
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleRejectClick(quote)}
                        data-testid={`reject-button-${quote.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" strokeWidth={1.5} />
                        Afwijzen
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <EmptyState
              icon={FileText}
              title="Geen offertes in review"
              description="Offertes binnen de standaard prijslijst worden automatisch goedgekeurd. Wanneer leveranciers afwijkende offertes indienen, verschijnen deze hier voor review."
            />
          </Card>
        )}

        {/* Reject Confirmation Dialog */}
        <ConfirmDialog
          open={rejectDialog.open}
          onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}
          title="Offerte afwijzen"
          description={`Weet u zeker dat u de offerte "${rejectDialog.quote?.action_title}" van ${rejectDialog.quote?.partner_name} wilt afwijzen? De leverancier ontvangt hiervan een notificatie.`}
          confirmLabel="Ja, afwijzen"
          cancelLabel="Annuleren"
          variant="warning"
          loading={processing}
          onConfirm={() => handleReview(rejectDialog.quote?.id, 'rejected')}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminQuotes;
